import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RefreshIcon from "./../../../../../assets/icons/arrow-path.svg?react";
import ArchiveIcon from "./../../../../../assets/icons/archive-box.svg?react";
import EditIcon from "./../../../../../assets/icons/pencil-square.svg?react";
import { useAppDispatch, useAppSelector } from "./../../../../../hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InstanceVolumeEditSchema } from "./../../../../../utils/validations";
import { DBSCatalog } from "./../../../../../types/database";
import {
  getDBSCatalogsAsync,
  selectDBSCatalogs,
  selectDBSCatalogsLoading,
} from "./../../../../../store/database/servicesSlice";
import {
  deleteInstancesVolumesAsync,
  getInstancesVolumeAsync,
  getInstancesVolumesAsync,
  handleSetSelectedInstanceVolume,
  selectInstancesSelectedVolume,
  selectInstancesSelectedVolumeLoading,
  selectInstancesVolumesActionLoading,
} from "./../../../../../store/instances/instancesVolumesSlice";
import { DeleteModal } from "./../../../../modals/QuestionModal";
import {
  InstancesEditVolumeUrl,
  InstancesVolumesUrl,
} from "./../../../../../utils/urls";
import { getDBSPriceAmount } from "../../../../databases/ServiceCreateComponents";
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Loading,
  Tag,
  Typography,
} from "djuno-design";
import { datacenterDetails } from "../../../../../utils/countries";

const VolumeOverviewTab = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const selectedVolume = useAppSelector(selectInstancesSelectedVolume);
  const selectedVolumeLoading = useAppSelector(
    selectInstancesSelectedVolumeLoading
  );

  const catalogs = useAppSelector(selectDBSCatalogs);
  const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);
  const actionLoading = useAppSelector(selectInstancesVolumesActionLoading);

  const [deleteVolume, setDeleteVolume] = useState<boolean>(false);
  const [filteredCatalogs, setFilteredCatalogs] = useState<Array<DBSCatalog>>(
    []
  );

  const methods = useForm({
    mode: "all",
    resolver: yupResolver(InstanceVolumeEditSchema),
    defaultValues: {
      capacity: 10,
      volumeName: "",
    },
  });

  const { setValue, watch } = methods;

  // watch form values
  const capacity = watch("capacity");
  // const volumeName = watch("volumeName");

  //get selected volume
  useEffect(() => {
    if (selectedVolume === null && id !== undefined) {
      dispatch(getInstancesVolumeAsync({ id }));
    }
  }, [dispatch, id, selectedVolume]);

  useEffect(() => {
    if (id) {
      dispatch(getInstancesVolumeAsync({ id: id })).then((action) => {
        if (action && action.type === "instances/volume/rejected") {
          navigate(InstancesVolumesUrl);
        }
      });
    }
  }, [dispatch, id, navigate]);

  //get first data
  useEffect(() => {
    if (!catalogs) {
      dispatch(getDBSCatalogsAsync("volume"));
    }
    // dispatch(getRegionsAsync());
  }, [catalogs, dispatch]);

  useEffect(() => {
    if (catalogs) {
      setFilteredCatalogs(
        catalogs.filter((a) => a.planCode.startsWith("volume."))
      );
    }
  }, [catalogs]);

  useEffect(() => {
    if (selectedVolume) {
      setValue("capacity", selectedVolume.size);
      setValue("volumeName", selectedVolume.name);
    }
  }, [selectedVolume, setValue]);

  const selectedCatalog = useMemo(() => {
    if (selectedVolume) {
      return filteredCatalogs.find(
        (a) => a.planCode === `volume.${selectedVolume.type}.consumption`
      );
    }
  }, [filteredCatalogs, selectedVolume]);

  const estimatedPrice = useMemo(() => {
    if (selectedCatalog) {
      const unitPrice = selectedCatalog.pricings[0].price;
      return unitPrice * 24 * 30 * capacity;
    }
    return 0;
  }, [capacity, selectedCatalog]);

  const OverviewCardSetting = () => {
    if (!selectedVolume) return null;
    return (
      <div className="flex items-center gap-2">
        <Button
          uiSize="medium"
          onClick={() =>
            dispatch(getInstancesVolumeAsync({ id: selectedVolume.id }))
          }
          className="group"
          tooltip={{ content: "Refresh" }}
        >
          <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
        </Button>
        <Dropdown
          anchor="bottom end"
          itemsClassName="!w-50 max-h-48 overflow-y-auto"
          menu={[
            {
              key: "1",
              label: (
                <div className="flex items-center gap-1">
                  <EditIcon className="w-4" />
                  Edit volume
                </div>
              ),
              onClick: (_, close) => {
                close();
                dispatch(handleSetSelectedInstanceVolume(selectedVolume));
                navigate(InstancesEditVolumeUrl(selectedVolume.id));
              },
            },

            { type: "divider" as const },
            {
              key: "end",
              label: (
                <div className="flex items-center gap-1">
                  {" "}
                  <ArchiveIcon className="w-4" />
                  Delete
                </div>
              ),
              danger: true,
              onClick: (_, close) => {
                close();
                setDeleteVolume(true);
              },
              disabled: selectedVolumeLoading,
            },
          ]}
        >
          <Button>Actions</Button>
        </Dropdown>
      </div>
    );
  };

  return (
    <>
      {selectedVolume && !selectedVolumeLoading && (
        <>
          <div className="mt-5">
            <Card title="Information" setting={<OverviewCardSetting />}>
              <div className="mt-2 grid gap-7 gap-x-10 md:grid-cols-2">
                <Flex items="center" className="gap-1">
                  <Typography.Text strong className="!text-sm">
                    Status:
                  </Typography.Text>
                  <Tag
                    className="text-xs !inline-block"
                    color={
                      selectedVolume.status === "available" ||
                      selectedVolume.status === "in-use"
                        ? "success"
                        : selectedVolume.status === "error" ||
                          selectedVolume.status === "error_backing-up" ||
                          selectedVolume.status === "error_deleting" ||
                          selectedVolume.status === "error_extending" ||
                          selectedVolume.status === "error_restoring"
                        ? "error"
                        : "processing"
                    }
                  >
                    {selectedVolume.status}
                  </Tag>
                </Flex>

                <Flex items="center" className="gap-1">
                  <Typography.Text strong className="!text-sm">
                    Region:
                  </Typography.Text>
                  <Typography.Text className="!text-sm">
                    {
                      datacenterDetails.find((dc) =>
                        selectedVolume.region.startsWith(dc.location)
                      )?.title
                    }
                  </Typography.Text>
                </Flex>

                <Flex items="center" className="gap-1">
                  <Typography.Text strong className="!text-sm">
                    Volume size:
                  </Typography.Text>
                  <Typography.Text className="!text-sm">
                    {selectedVolume.size}
                  </Typography.Text>
                </Flex>

                <Flex items="center" className="gap-1">
                  <Typography.Text strong className="!text-sm">
                    Estimated amount:
                  </Typography.Text>
                  <Typography.Text className="!text-sm">
                    {`$${getDBSPriceAmount(estimatedPrice)} ex. VAT/month`}
                  </Typography.Text>
                </Flex>

                <Flex items="center" className="gap-1">
                  <Typography.Text strong className="!text-sm">
                    Volume type:
                  </Typography.Text>
                  <Typography.Text className="!text-sm">
                    {selectedVolume.type}
                  </Typography.Text>
                </Flex>
              </div>
            </Card>
          </div>

          <DeleteModal
            title={`Delete ${selectedVolume?.name}`}
            isOpen={deleteVolume}
            onClose={() => setDeleteVolume(false)}
            description=""
            confirmButtonText="Confirm"
            confirmButtonType="danger"
            confirmButtonClassName="w-full"
            loading={actionLoading}
            onConfirm={async () => {
              if (!deleteVolume || !selectedVolume) return;

              const action = await dispatch(
                deleteInstancesVolumesAsync({
                  id: selectedVolume.id,
                  region: selectedVolume.region,
                })
              );

              if (action.type === "instances/volumes/delete/fulfilled") {
                dispatch(getInstancesVolumesAsync({}));
                navigate(InstancesVolumesUrl);
                setDeleteVolume(false);
              }
            }}
            confirmString={selectedVolume.name}
          />
        </>
      )}
      {(selectedVolumeLoading || catalogsLoading) && (
        <Flex items="center" justify="center" className="!min-h-[300px] w-full">
          <Loading borderSize={2} />
        </Flex>
      )}
    </>
  );
};

export default VolumeOverviewTab;
